import { useAutoTranslation, useUserStore } from "@/entities"
import {
  useConfirmCode,
  useFinalCode,
  useNewEmail,
  useRequestEmailCode,
} from "./model"
import {
  TypographyError,
  TypographyH2,
  TypographyP,
} from "@/components/ui/typography"
import { Button } from "@/components/ui/button"
import { useEffect, useState } from "react"
import { Controller } from "react-hook-form"
import { Input } from "@/components/ui/input"
import { InputGroup, InputGroupError } from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"

export const ChangeEmail = () => {
  const { t } = useAutoTranslation()
  const email = useUserStore(s => s.email)!

  const [firstRightCode, setFirstRightCode] = useState("")

  const handleFirstCodeRes = (code: string) => {
    setFirstRightCode(code)
  }

  const {
    handleSubmit,
    setValue: setEmailValue,
    codeSending,
    codeSent,
    setCodeSent,
    resetRequestEmailCode,
  } = useRequestEmailCode(email)

  const {
    handleSubmit: codeSubmit,
    errors: codeErrors,
    control: codeControl,
    isLoading: codeLoading,
    codeWrong,
    setCodeWrong,
    setValue,
    setConfirmed,
    confirmed,
    resetConfirmFirstCode,
  } = useConfirmCode(handleFirstCodeRes)

  const {
    handleSubmit: emailSubmit,
    isLoading: emailLoading,
    errors: emailErrors,
    control: emailControl,
    setEmailProvided,
    newEmailProvided,
    selectedEmail,
    setSelectedEmail,
    resetNewEmail,
  } = useNewEmail(firstRightCode)

  const {
    handleSubmit: submitFinalCode,
    codeWrong: finalCodeWrong,
    setCodeWrong: setFinalCodeWrong,
    setValue: setFinalValue,
    control: finalControl,
    isLoading: isFinalLoading,
    errors: finalErrors,
    setDone,
    done,
    resetFinalCode,
  } = useFinalCode()

  const handleCancel = () => {
    setCodeSent(false)
    setConfirmed(false)
    setCodeWrong("")
    setEmailProvided(false)
    setSelectedEmail("")
    setFinalValue("code", "")
    setValue("code", "")
    setFinalCodeWrong("")
    setDone(false)
    resetConfirmFirstCode()
    resetRequestEmailCode()
    resetNewEmail()
    resetFinalCode()
  }

  const handleFinish = () => {
    setEmailValue("email", selectedEmail)
    handleCancel()
  }

  useEffect(() => {
    if (done) {
      handleFinish()
    }
  }, [done])

  const RequestEmailCode = (
    <div>
      <TypographyH2>
        {t(DISCOVERED_TRANSLATION_KEYS.EMAIL)} {email}
      </TypographyH2>
      <div className="flex flex-col gap-2">
        <TypographyP className="m-0!">
          {t(
            DISCOVERED_TRANSLATION_KEYS.IN_ORDER_TO_CHANGE_YOUR_EMAIL_WE_NEED_TO_SEND_ACONFIRMATION_CODE_TO_YOUR_CURRENT_EMAIL
          )}
        </TypographyP>
        <TypographyP className="m-0!">
          {t(
            DISCOVERED_TRANSLATION_KEYS.FURTHER_YOU_WILL_PROVIDE_ANEW_EMAIL_AND_CONFIRM_IT_WITH_THE_CODE
          )}
        </TypographyP>
        <Button
          className="mt-4 w-fit"
          onClick={handleSubmit}
          isLoading={codeSending}
        >
          {t(DISCOVERED_TRANSLATION_KEYS.START_CHANGING_EMAIL)}
        </Button>
      </div>
    </div>
  )

  const ConfirmCode = (
    <form onSubmit={codeSubmit}>
      <TypographyH2>{t(LANG_KEYS.ENTER_CONFIRMATION_CODE)}</TypographyH2>
      <TypographyP className="m-0!">
        {t(LANG_KEYS.WE_HAVE_SENT_A_CONFIRMATION_CODE_TO_YOUR_CURRENT_EMAIL)}
      </TypographyP>

      <Controller
        control={codeControl}
        name="code"
        rules={{
          required: {
            value: true,
            message: t(DISCOVERED_TRANSLATION_KEYS.CODE_IS_REQUIRED),
          },
        }}
        render={({ field }) => (
          <InputGroup className="mt-4 mb-6 flex gap-2 p-2 pr-0">
            <Label htmlFor="first-code">
              {t(DISCOVERED_TRANSLATION_KEYS.CODE)}
            </Label>
            <Input
              id="first-code"
              value={field.value}
              autoFocus
              onChange={field.onChange}
              aria-invalid={!!codeErrors.code}
            />
            <InputGroupError>{codeErrors.code?.message}</InputGroupError>
          </InputGroup>
        )}
      />
      {!!codeWrong && (
        <TypographyError className="mb-4">{codeWrong}</TypographyError>
      )}

      <div className="flex gap-2">
        <Button onClick={handleCancel} variant="destructive">
          {t(LANG_KEYS.CANCEL)}
        </Button>
        <Button type="submit" isLoading={codeLoading}>
          {t(LANG_KEYS.CONFIRM)}
        </Button>
      </div>
    </form>
  )

  const ChangeEmail = (
    <form onSubmit={emailSubmit}>
      <TypographyH2>{t(LANG_KEYS.ENTER_NEW_EMAIL)}</TypographyH2>
      <TypographyP className="m-0!">
        {t(LANG_KEYS.YOUR_ACCOUNT_WILL_BE_LINKED_TO_THE_SPECIFIED_EMAIL)}
      </TypographyP>

      <Controller
        name="email"
        control={emailControl}
        rules={{
          required: {
            value: true,
            message: t(DISCOVERED_TRANSLATION_KEYS.EMAIL_IS_REQUIRED),
          },
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t(LANG_KEYS.INVALID_EMAIL_FORMAT),
          },
        }}
        render={({ field }) => (
          <InputGroup className="mt-4 mb-6 flex gap-2 p-2 pr-0">
            <Label htmlFor="new-email" className="whitespace-nowrap">
              {t(LANG_KEYS.NEW_EMAIL)}
            </Label>
            <Input
              id="new-email"
              value={field.value}
              autoFocus
              onChange={field.onChange}
              aria-invalid={!!emailErrors.email}
            />
            <InputGroupError>{emailErrors.email?.message}</InputGroupError>
          </InputGroup>
        )}
      />

      <div className="flex gap-2">
        <Button onClick={handleCancel} variant="destructive">
          {t(LANG_KEYS.CANCEL)}
        </Button>
        <Button type="submit" isLoading={emailLoading}>
          {t(LANG_KEYS.CONFIRM)}
        </Button>
      </div>
    </form>
  )

  const NewEmailCode = (
    <form onSubmit={submitFinalCode}>
      <TypographyH2>{t(LANG_KEYS.FINAL_STEP)}</TypographyH2>
      <TypographyP className="m-0!">
        {t(
          LANG_KEYS.A_CONFIRMATION_CODE_HAS_BEEN_SENT_TO_YOUR_NEWLY_PROVIDED_EMAIL_ADDRESS_PLEASE_BE_AWARE_THAT_THIS_ACTION_ONCE_COMPLETED_CANNOT_BE_REVERSED
        )}
      </TypographyP>

      <Controller
        name="code"
        control={finalControl}
        rules={{
          required: {
            value: true,
            message: t(DISCOVERED_TRANSLATION_KEYS.CODE_IS_REQUIRED),
          },
        }}
        render={({ field }) => (
          <InputGroup className="mt-4 mb-6 flex gap-2 p-2 pr-0">
            <Label htmlFor="final-code" className="whitespace-nowrap">
              {t(DISCOVERED_TRANSLATION_KEYS.CODE)}
            </Label>
            <Input
              id="final-code"
              value={field.value}
              autoFocus
              onChange={field.onChange}
              aria-invalid={!!finalErrors.code}
            />
            <InputGroupError>{finalErrors.code?.message}</InputGroupError>
          </InputGroup>
        )}
      />

      {!!finalCodeWrong && (
        <TypographyError className="mb-4">{finalCodeWrong}</TypographyError>
      )}

      <div className="flex gap-2">
        <Button onClick={handleCancel} variant="destructive">
          {t(LANG_KEYS.CANCEL)}
        </Button>
        <Button type="submit" isLoading={isFinalLoading}>
          {t(LANG_KEYS.CONFIRM)}
        </Button>
      </div>
    </form>
  )

  return (
    <div>
      {!codeSent && !confirmed && RequestEmailCode}
      {codeSent && !confirmed && ConfirmCode}
      {codeSent && confirmed && !newEmailProvided && ChangeEmail}
      {codeSent && confirmed && newEmailProvided && NewEmailCode}
    </div>
  )
}
